import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { GatsbyImage } from 'gatsby-plugin-image';
import {
  Section,
  Container,
  Col,
  Grid,
  AnchorText,
  Overlay,
  SectionHeader,
} from '../../common';

const StyledH3 = styled.h3`
  color: ${(props) => props.$color} !important;
  :hover {
    color: ${(props) => props.$colorHover} !important;
  }
`;

const getListLength = (listType, services, pages) =>
  listType === 'serviceList'
    ? services?.length
    : pages?.filter((page) => page?.type === 'service')?.length;

const getGridClasses = (listLength, noServiceSectionImage) =>
  listLength > 4
    ? 'lg:grid-cols-4 gap-x-8 gap-y-12'
    : noServiceSectionImage
    ? 'gap-12 lg:grid-cols-2'
    : 'gap-12';

const ServiceItem = ({
  item,
  type,
  imageKey,
  sanity,
  noServiceSectionImage,
  listHeadlineSize,
  colors,
  section,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsHovered(false);
    }
  };
  return (
    <Col key={item?.slug?.current} classes="relative">
      <div
        className="relative justify-between overflow-hidden rounded-xl hover:shadow-2xl lg:flex-col"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <AnchorText
          sanity={sanity}
          type="internal"
          to={item?.link?.slug?.current || item?.slug?.current}
          classes={`relative ${
            noServiceSectionImage ? 'py-24' : 'py-14'
          } px-8 flex lg:flex-col justify-between`}
        >
          <div className="absolute inset-0 z-0">
            <GatsbyImage
              image={item?.[imageKey]?.asset?.gatsbyImageData}
              alt={item?.image?.alt}
              className="h-full w-full object-cover"
            />
            <Overlay
              hasHover={isHovered}
              overlayType={colors?.hero?.overlayImageType}
              overlayAngle={colors?.hero?.overlayImageAngle}
              overlayWidth={colors?.hero?.overlayImageWidth}
              overlayHeight={colors?.hero?.overlayImageHeight}
              bgColor={colors?.neutral?.default}
              bgColorHover={colors?.neutral?.dark}
              bgColorStart={colors?.neutral?.darker}
              bgColorMid={colors?.neutral?.dark}
              bgColorEnd={colors?.neutral?.default}
            />
          </div>
          <div className="relative z-10 m-auto px-4 text-center">
            <StyledH3
              $color={colors?.accent?.lighter}
              $colorHover={colors?.accent?.light}
              className={`${listHeadlineSize} font-bold`}
            >
              <span className="drop-shadow-darker">{item?.name}</span>
            </StyledH3>
          </div>
        </AnchorText>
      </div>
      {/* <div
        className={`absolute left-0 right-0 top-0 mx-auto -mt-6 h-12 w-12 rounded-full shadow-xl ${
          section?.iconType === 'solid' ? 'bg-white p-2' : 'p-0'
        }`}
      >
        <GatsbyImage
          image={sanity?.logos?.favicon?.asset?.gatsbyImageData}
          alt={sanity?.logos?.favicon?.alt}
          className="h-full w-full rounded-full object-cover"
        />
      </div> */}
    </Col>
  );
};

const ServicesOverview = ({ sanity, section }) => {
  const { colors, services, pages } = sanity;
  const noServiceSectionImage = !section?.image?.asset?.gatsbyImageData;
  const listHeadlineSize = section?.listHeadlineSize?.fontSize || 'text-3xl';

  const listLength = getListLength(section?.listType, services, pages);
  const showImage = listLength <= 4 && !noServiceSectionImage;
  const gridClasses = getGridClasses(listLength, noServiceSectionImage);

  const servicesToDisplay =
    section?.listType === 'serviceList'
      ? services?.sort((a, b) => a?.order - b?.order)
      : pages
          ?.filter((page) => page?.type === 'service')
          ?.sort((a, b) => a?.order - b?.order);

  return (
    <Section
      idName="services-overview"
      bgColor={colors?.neutral?.lighter}
      h2Color={colors?.neutral?.darker}
    >
      <Container
        padding="none"
        classes="max-w-md px-4 text-center sm:max-w-3xl lg:max-w-7xl"
      >
        <SectionHeader
          sanity={sanity}
          tagline={section?.header?.tagline}
          headline={section?.header?.headline}
          description={section?.header?._rawDescription}
        />
        <Grid classes="lg:grid-cols-2 gap-24 flex justify-center items-center">
          {showImage && (
            <Col classes="hidden lg:block w-full">
              <GatsbyImage
                image={section?.image?.asset?.gatsbyImageData}
                alt={section?.image?.alt}
                className={`drop-shadow-2xl ${
                  section?.imageType === 'solid'
                    ? 'object-fit h-full w-full rounded-xl shadow-2xl drop-shadow-lg'
                    : ''
                }`}
              />
            </Col>
          )}
          <Col classes={showImage ? '' : 'lg:col-span-2'}>
            <Grid classes={gridClasses}>
              {servicesToDisplay?.map((item) => (
                <ServiceItem
                  key={item?.slug?.current}
                  item={item}
                  type={section?.listType}
                  imageKey={
                    section?.listType === 'serviceList' ? 'image' : 'heroBgImg'
                  }
                  sanity={sanity}
                  noServiceSectionImage={noServiceSectionImage}
                  colors={colors}
                  section={section}
                  listHeadlineSize={listHeadlineSize}
                />
              ))}
            </Grid>
          </Col>
        </Grid>
      </Container>
    </Section>
  );
};

export { ServicesOverview };
